import type { Benchmark, CostValue, PriceIndex, RegionalFactor } from '../../api';
import { DESTATIS_UNSURE, formatAmount, formatQuarter } from '../../format';

/**
 * Bringing a catalog value to the project's place and date (AP-80), as a
 * formula of named values so the conversion stays visible and one change of
 * the factor or the index reaches every line:
 *   118,00 * Regionalfaktor * BPI_aktuell / BPI_2025Q1
 */

export const FACTOR_NAME = 'Regionalfaktor';
export const INDEX_NOW = 'BPI_aktuell';
export const indexName = (period: string, prefix = 'BPI'): string => `${prefix}_${period.replace('-', '')}`;
/** Outdoor facilities (cost group 500) have their own index where one is recorded. */
export const OUTDOOR_SERIES = 'Außenanlagen';

/** Only the total line without VAT enters the conversion: cost plans are net. */
const usable = (i: PriceIndex, series: string): boolean => i.buildingType === series && (i.work ?? 'insgesamt') === 'insgesamt' && (i.vat ?? 'ohne') === 'ohne';

/** "1. Quartal 2025", "Q1/2025", "I/2025", "2025-Q1" → "2025-Q1"; anything else null. */
export function parseQuarter(text: string | null | undefined): string | null {
  const s = (text ?? '').trim();
  let m = /^(\d{4})\s*-?\s*Q([1-4])$/i.exec(s);
  if (m) return `${m[1]}-Q${m[2]}`;
  m = /^([1-4])\.?\s*Quartal\s*(\d{4})$/i.exec(s) ?? /^Q([1-4])\s*[/ ]\s*(\d{4})$/i.exec(s);
  if (m) return `${m[2]}-Q${m[1]}`;
  m = /^(IV|III|II|I)\s*[/.]\s*(\d{4})$/i.exec(s);
  if (m) return `${m[2]}-Q${({ I: 1, II: 2, III: 3, IV: 4 } as Record<string, number>)[m[1]!.toUpperCase()]}`;
  return null;
}

const norm = (s: string): string => s.toLowerCase().replace(/[^a-zäöüß0-9]+/g, ' ').trim();

/**
 * The regional factor for a project location: an exact district name wins;
 * otherwise a single district that starts with the location ("Aachen" →
 * "Aachen, Städteregion"). Several candidates: no choice, the user picks.
 */
export function matchDistrict(location: string | null | undefined, factors: readonly RegionalFactor[]): { match: RegionalFactor | null; candidates: RegionalFactor[] } {
  const loc = norm(location ?? '');
  if (!loc) return { match: null, candidates: [] };
  const exact = factors.filter((f) => norm(f.district) === loc);
  if (exact.length === 1) return { match: exact[0]!, candidates: exact };
  const partial = exact.length > 1 ? exact : factors.filter((f) => norm(f.district).startsWith(loc) || loc.startsWith(norm(f.district)));
  return { match: partial.length === 1 ? partial[0]! : null, candidates: partial };
}

/** The newest value of one series (a quarter sorts after its year's annual value). */
export function latestIndex(indices: readonly PriceIndex[], type: string): PriceIndex | null {
  return indices.filter((i) => usable(i, type)).sort((a, b) => b.period.localeCompare(a.period))[0] ?? null;
}

/** The value for a quarter, or the annual value of that year when the quarter is missing. */
export function indexFor(indices: readonly PriceIndex[], type: string, quarter: string): PriceIndex | null {
  const own = indices.filter((i) => usable(i, type));
  return own.find((i) => i.period === quarter) ?? own.find((i) => i.period === quarter.slice(0, 4)) ?? null;
}

export interface NeededValue {
  name: string;
  label: string;
  formula: string;
}

export interface Conversion {
  formula: string;
  /** Named values the formula uses that the plan does not have yet. */
  create: NeededValue[];
  /** What was not converted and why, for the line's source note. */
  notes: string[];
}

/** A number as the formula field takes it: decimal comma, no thousands separator, no trailing zeros. */
const num = (v: number, digits = 3): string => v.toLocaleString('de-DE', { maximumFractionDigits: digits, useGrouping: false });

/**
 * The formula for a picked catalog value. `factor` is what the location
 * suggests, used only if the plan has no Regionalfaktor yet.
 */
export function convertBenchmark(args: {
  value: number;
  benchmark: Pick<Benchmark, 'region' | 'priceDate'> & Partial<Pick<Benchmark, 'costGroup'>>;
  planValues: readonly Pick<CostValue, 'name'>[];
  factor: RegionalFactor | null;
  indices: readonly PriceIndex[];
  indexType: string;
}): Conversion {
  const has = new Set(args.planValues.map((v) => v.name.toLowerCase()));
  let formula = formatAmount(args.value).replace(/\./g, '');
  const create: NeededValue[] = [];
  const notes: string[] = [];

  const federal = !args.benchmark.region || /bundes/i.test(args.benchmark.region);
  if (!federal) {
    notes.push(`kein Regionalfaktor: Kennwert gilt schon für ${args.benchmark.region}`);
  } else if (has.has(FACTOR_NAME.toLowerCase())) {
    formula += ` * ${FACTOR_NAME}`;
  } else if (args.factor) {
    formula += ` * ${FACTOR_NAME}`;
    create.push({
      name: FACTOR_NAME, formula: num(args.factor.factor),
      label: `${args.factor.district} (${[args.factor.source, args.factor.edition].filter(Boolean).join(' ')})`,
    });
  } else {
    notes.push('kein Regionalfaktor: Standort ohne passenden Kreis');
  }

  const then = parseQuarter(args.benchmark.priceDate);
  const outdoor = (args.benchmark.costGroup ?? '').startsWith('5') && args.indices.some((i) => usable(i, OUTDOOR_SERIES));
  const series = outdoor ? OUTDOOR_SERIES : args.indexType;
  const prefix = outdoor ? 'BPI_AA' : 'BPI';
  const nowName = `${prefix}_aktuell`;
  const now = latestIndex(args.indices, series);
  const past = then ? indexFor(args.indices, series, then) : null;
  if (!then) {
    notes.push(`kein Baupreisindex: Preisstand „${args.benchmark.priceDate ?? ''}“ nicht als Quartal lesbar`);
  } else if (!now || !past) {
    notes.push(`kein Baupreisindex: ${series} ${formatQuarter(then)} nicht erfasst`);
  } else if (now.period !== past.period || has.has(nowName.toLowerCase())) {
    const pastName = indexName(past.period, prefix);
    formula += ` * ${nowName} / ${pastName}`;
    const label = (i: PriceIndex) => `Baupreisindex ${i.buildingType}, ${formatQuarter(i.period)}${i.base ? ` (${i.base})` : ''}, ohne USt`;
    if (!has.has(nowName.toLowerCase())) create.push({ name: nowName, formula: num(now.value, 2), label: label(now) });
    if (!has.has(pastName.toLowerCase())) create.push({ name: pastName, formula: num(past.value, 2), label: label(past) });
    if (past.period !== then) notes.push(`Baupreisindex: Jahreswert ${past.period} statt ${formatQuarter(then)}`);
    for (const i of [now, past]) {
      const unsure = (i.flag ?? '').split(' ').map((f) => DESTATIS_UNSURE[f]).filter(Boolean);
      if (unsure.length) notes.push(`Baupreisindex ${formatQuarter(i.period)}: ${unsure.join(', ')} (Destatis)`);
    }
    if (outdoor) notes.push('Baupreisindex der Außenanlagen (KG 500)');
  }

  return { formula, create, notes };
}
